import { hasNodeChildSymbolNode } from "@/models/nodes/utils";

import type { SelectSuggestionFn, Suggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { LabelNodePosition, NodeJson } from "@corca/data";

function hasLabelInNodesJson(nodes: NodeJson[] | undefined, position: LabelNodePosition): boolean {
  if (!nodes) return false;
  for (const node of nodes) {
    if (node.type === "label" && node.props?.position === position) {
      return true;
    }
    if (node.children && hasLabelInNodesJson(node.children, position)) {
      return true;
    }
  }
  return false;
}

// Defined-variable suggestions carry a live `node` and a synthesized subscript
// label, so they keep the normal subscript-building flow instead of jumping.
export function jumpToLabel(
  doc: DocumentModel,
  suggestion: Suggestion | undefined,
  onSelect: SelectSuggestionFn,
  e: KeyboardEvent,
): boolean {
  if (doc.editor.editorState.latexInput) return false;
  if (e.key !== "_" && e.key !== "^") return false;
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  if (suggestion?.type !== "single") return false;
  if (suggestion.item.node) return false;

  const position: LabelNodePosition = e.key === "_" ? "right-bottom" : "right-top";
  const willHaveLabel =
    suggestion.item.labels?.some((l) => l.position === position) ||
    hasLabelInNodesJson(suggestion.item.nodesJson, position);
  if (!willHaveLabel) return false;

  doc.transact(() => {
    onSelect(suggestion, undefined, true);

    let node = doc.cursor.nodeBeforeCursor ?? doc.cursor.node;
    while (node && !hasNodeChildSymbolNode(node)) {
      node = node.parent;
    }
    const labelNode = node?.symbolNode?.labelNodes.find((l) => l.position === position);
    if (labelNode?.inlineNode) {
      doc.input.reset(true);
      doc.cursor.moveTo(labelNode.inlineNode, labelNode.inlineNode.nodes.length);
    }
  }, "useSuggestionsActions:postCommitLabel");

  return true;
}
